import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  AddressBookResolver,
  AddressResolver,
  AuctionBidsResolver,
  AuctionResolver,
  AuthGuard,
  BalanceResolver,
  CardTokenResolver,
  CountryResolver,
  FGBPreLoadingStrategy,
  FantasyGameResolver,
  FantasyHistoryResolver,
  LottoResolver,
  LottoWalletResolver,
  MaintenanceGuard,
  MemberCardResolver,
  MemberLoyaltyStatusResolver,
  MemberResolver,
  PopulationGuard,
  PreviewResolver,
  PurchaseResolver,
  SeatingDetailResolver,
  SurveyResolver,
  TicketResolver,
  TransactionSuccessGuard,
  accountVerificationGuard,
  firstTimeUserGuard,
  gameWithTicketResolver,
  groupedWalletResolver,
  ticketTransferGuard,
} from '@fgb/core';

// General
import { ContactUsPageComponent } from './pages/contact-us-page/contact-us-page.component';
import { FaqPageComponent } from './pages/faq-page/faq-page.component';
import { MasterPageComponent } from './pages/master/master-page/master-page.component';
import { NotFoundPageComponent } from './pages/not-found-page/not-found-page.component';
import { TermsPageComponent } from './pages/terms-page/terms-page.component';

// Account
import { AccountDetailsPageComponent } from './pages/account-details-page/account-details-page.component';
import { CardPageComponent } from './pages/card-page/card-page.component';

// Events & Tickets
import { ContactsPageComponent } from './pages/contacts-page/contacts-page.component';

// Unauthenticated
import { SingleSignOnComponent } from './modules/user/components/single-sign-on/single-sign-on.component';
import { LoginAccountsPageComponent } from './pages/login-accounts-page/login-accounts-page.component';
import { LoginPageComponent } from './pages/login-page/login-page.component';
import { MaintenancePageComponent } from './pages/maintenance-page/maintenance-page.component';

// Registration
import { RegistrationLayoutPageComponent } from './modules/registration/pages/registration-layout-page/registration-layout-page.component';

// Club Pages
import { provideRegistrationConfig } from '@fgb/portal-component-library/src/lib/registration';
import { TicketTransferPageComponent } from 'src/app/pages/ticket-transfer-page/ticket-transfer-page.component';
import { TicketTransferSuccessPageComponent } from 'src/app/pages/ticket-transfer-page/ticket-transfer-success-page/ticket-transfer-success-page.component';
import { TicketsPageComponent } from 'src/app/pages/ticket-transfer-page/tickets-page/tickets-page.component';
import { TransferConfirmPageComponent } from 'src/app/pages/ticket-transfer-page/transfer-confirm-page/transfer-confirm-page.component';
import { AuthCallbackComponent } from './modules/user/components/auth/auth-callback/auth-callback.component';
import { V1SingleSignOnComponent } from './modules/user/components/v1-single-sign-on/v1-single-sign-on.component';
import { ActivityPageComponent } from './pages/activity-page/activity-page.component';
import { ChallengesPageComponent } from './pages/challenges-page/challenges-page.component';
import { EarnPageComponent } from './pages/earn-page/earn-page.component';
import { EventsPageComponent } from './pages/events-page/events-page.component';
import { HomePageComponent } from './pages/home-page/home-page.component';
import { LottoDetailPageComponent } from './pages/lotto-detail-page/lotto-detail-page.component';
import { LottoWalletDetailsPageComponent } from './pages/lotto-wallet-details-page/lotto-wallet-details-page.component';
import { LottosPageComponent } from './pages/lottos-page/lottos-page.component';
import { LottosSuccessPageComponent } from './pages/lottos-page/lottos-success-page/lottos-success-page.component';
import { MarketplaceDetailPageComponent } from './pages/marketplace-detail-page/marketplace-detail-page.component';
import { MarketplacePageComponent } from './pages/marketplace-page/marketplace-page.component';
import { MarketplacePurchaseSuccessPageComponent } from './pages/marketplace-page/marketplace-purchase-success-page/marketplace-purchase-success-page.component';
import { OfferDetailsPageComponent } from './pages/offer-details-page/offer-details-page.component';
import { OffersPageComponent } from './pages/offers-page/offers-page.component';
import { RewardsLayoutComponent } from './pages/rewards-layout/rewards-layout.component';
import { WalletPageComponent } from './pages/wallet-page/wallet-page.component';
import { WorksheetPageComponent } from './pages/worksheet-page/worksheet-page.component';
import { KeyCloakCallbackComponent } from './shared/components/providers/key-cloak/key-cloak-callback/key-cloak-callback.component';
import { AuctionsPageComponent } from './pages/auctions-page/auctions-page.component';
import { AuctionDetailPageComponent } from './pages/auction-detail-page/auction-detail-page.component';
import { WalletClaimedPageComponent } from './pages/wallet-claimed-page/wallet-claimed-page.component';
import { AuctionWalletDetailsPageComponent } from './pages/auction-wallet-details-page/auction-wallet-details-page.component';
import { MarketplaceWalletPageComponent } from './pages/marketplace-wallet-page/marketplace-wallet-page.component';
import { GamesLayoutComponent } from './pages/games-layout/games-layout.component';
import { GamesPageComponent } from './pages/games-page/games-page.component';
import { FantasyGamesPageComponent } from './pages/fantasy-games-page/fantasy-games-page.component';
import { FantasyPlayerSelectPageComponent } from './pages/fantasy-player-select-page/fantasy-player-select-page.component';
import { PredictorPageComponent } from './pages/predictor-page/predictor-page.component';
import { PollsPageComponent } from './pages/polls-page/polls-page.component';
import { QuizPageComponent } from './pages/quiz-page/quiz-page.component';
import { ScorecardDescriptionComponent } from '@fgb/portal-component-library/src/lib/loyalty/scorecards';
import { LeaderboardPageComponent } from './pages/leaderboard-page/leaderboard-page.component';
import { SalesForceCallbackComponent } from './shared/components/providers/sales-force/sales-force-callback/sales-force-callback.component';
import { ActivateAccountPageComponent } from 'src/app/pages/account-activation/activate-account-page/activate-account-page.component';
import { AccountVerifiedPageComponent } from 'src/app/pages/account-activation/account-verified-page/account-verified-page.component';
import { CartCheckoutPageComponent } from './pages/cart-checkout-page/cart-checkout-page.component';
import { CartCheckoutSuccessPageComponent } from './pages/cart-checkout-page/cart-checkout-success-page/cart-checkout-success-page.component';
import { CartCheckoutResultsPageComponent } from './pages/cart-checkout-page/cart-checkout-results-page/cart-checkout-results-page.component';

const routes: Routes = [
  {
    path: '',
    canActivate: [AuthGuard, firstTimeUserGuard(), accountVerificationGuard('/account-verification')],
    canActivateChild: [MaintenanceGuard],
    component: MasterPageComponent,
    resolve: {
      member: MemberResolver,
      balances: BalanceResolver,
      membercard: MemberCardResolver,
      cardToken: CardTokenResolver,
    },
    children: [
      {
        path: '',
        resolve: {
          memberCard: MemberCardResolver,
          address: AddressResolver,
          surveys: SurveyResolver,
        },
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
        component: HomePageComponent,
      },
      {
        path: 'cards',
        component: CardPageComponent,
      },

      {
        path: 'leaderboard',
        component: LeaderboardPageComponent,
      },

      {
        path: 'challenges',
        component: ChallengesPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      { path: 'scorecard-description/:id', component: ScorecardDescriptionComponent },

      {
        path: 'account',
        component: AccountDetailsPageComponent,
        resolve: {
          member: MemberResolver,
          balances: BalanceResolver,
          seating: SeatingDetailResolver,
          memberLoyaltyStatus: MemberLoyaltyStatusResolver,
        },
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      {
        path: 'activity',
        component: ActivityPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      {
        path: 'offers',
        children: [
          { path: '', component: OffersPageComponent },
          {
            path: 'details/:offerId',
            component: OfferDetailsPageComponent,
            canActivate: [PopulationGuard],
            data: {
              exclude: ['Ticket Transfer'],
              popRedirect: 'ticketTransfer',
            },
          },
        ],
      },

      {
        path: 'rewards',
        component: RewardsLayoutComponent,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: 'marketplace',
          },

          {
            path: 'auctions',
            children: [
              {
                path: '',
                resolve: { auctions: AuctionResolver },
                component: AuctionsPageComponent,
                canActivate: [PopulationGuard],
                data: {
                  exclude: ['Ticket Transfer'],
                  popRedirect: 'ticketTransfer',
                },
              },
              {
                path: 'details/:id',
                resolve: {
                  balances: BalanceResolver,
                  auctions: AuctionResolver,
                },
                canActivate: [PopulationGuard],
                data: {
                  exclude: ['Ticket Transfer'],
                  popRedirect: 'ticketTransfer',
                },
                component: AuctionDetailPageComponent,
              },
            ],
          },
          {
            path: 'marketplace',
            children: [
              {
                path: '',
                component: MarketplacePageComponent,
                canActivate: [PopulationGuard],
                data: {
                  exclude: ['Ticket Transfer'],
                  popRedirect: 'ticketTransfer',
                },
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: {
                      balances: BalanceResolver,
                      cardTokens: CardTokenResolver,
                      country: CountryResolver,
                    },
                    canActivate: [PopulationGuard],
                    data: {
                      exclude: ['Ticket Transfer'],
                      popRedirect: 'ticketTransfer',
                    },
                    component: MarketplaceDetailPageComponent,
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: MarketplacePurchaseSuccessPageComponent,
                resolve: {
                  balances: BalanceResolver,
                  purchase: PurchaseResolver,
                },
              },
            ],
          },
          {
            path: 'events',
            component: EventsPageComponent,
            canActivate: [PopulationGuard],
            data: {
              exclude: ['Ticket Transfer'],
              popRedirect: 'ticketTransfer',
            },
          },

          {
            path: 'raffles',
            children: [
              {
                path: '',
                component: LottosPageComponent,
                resolve: { lottos: LottoResolver, balances: BalanceResolver },
                canActivate: [PopulationGuard],
                data: {
                  exclude: ['Ticket Transfer'],
                  popRedirect: 'ticketTransfer',
                },
              },
              {
                path: 'details/:id',
                children: [
                  {
                    path: '',
                    resolve: { balances: BalanceResolver, lottos: LottoResolver },
                    component: LottoDetailPageComponent,
                    canActivate: [PopulationGuard],
                    data: {
                      exclude: ['Ticket Transfer'],
                      popRedirect: 'ticketTransfer',
                    },
                  },
                ],
              },
              {
                path: 'purchase/success',
                canActivate: [TransactionSuccessGuard],
                component: LottosSuccessPageComponent,
              },
            ],
          },
          {
            path: 'wallet',
            children: [
              {
                path: '',
                component: WalletPageComponent,
                canActivate: [PopulationGuard],
                data: {
                  exclude: ['Ticket Transfer'],
                  popRedirect: 'ticketTransfer',
                },
              },
              {
                path: 'details/:id',
                component: MarketplaceWalletPageComponent,
                resolve: { purchases: PurchaseResolver },
              },
              {
                path: 'raffledetails/:id',
                component: LottoWalletDetailsPageComponent,
                resolve: { lottoWalletItems: LottoWalletResolver },
              },
            ],
            resolve: {
              walletItems: groupedWalletResolver({ redeemed: false }),
            },
          },
          {
            path: 'claimed',
            children: [
              {
                path: '',
                component: WalletClaimedPageComponent,
                resolve: {
                  purchases: PurchaseResolver,
                  lottoWalletItems: LottoWalletResolver,
                  auctionBids: AuctionBidsResolver,
                },
                canActivate: [PopulationGuard],
                data: {
                  exclude: ['Ticket Transfer'],
                  popRedirect: 'ticketTransfer',
                },
              },
            ],
            resolve: {
              walletItems: groupedWalletResolver({ redeemed: true }),
            },
          },
          {
            path: 'bids',
            children: [
              {
                path: 'details/:id',
                component: AuctionWalletDetailsPageComponent,
                resolve: { bids: AuctionBidsResolver },
              },
            ],
            resolve: {
              walletItems: groupedWalletResolver({ redeemed: true }),
            },
          },
        ],
      },
      {
        path: 'gallery',
        component: WorksheetPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },

      {
        path: 'games',
        resolve: {
          surveys: SurveyResolver,
          games: FantasyGameResolver,
        },
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
        component: GamesLayoutComponent,
        children: [
          { path: '', component: GamesPageComponent },
          {
            path: 'pickaplayer',
            component: FantasyGamesPageComponent,
            resolve: { games: FantasyGameResolver, history: FantasyHistoryResolver },
          },
          {
            path: 'pickaplayer/:id',
            component: FantasyPlayerSelectPageComponent,
            resolve: { games: FantasyGameResolver },
          },
          { path: 'predictor', component: PredictorPageComponent },
          { path: 'polls', component: PollsPageComponent },
          { path: 'trivia', component: QuizPageComponent },
        ],
      },

      {
        path: 'earn',
        component: EarnPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      {
        path: 'faq',
        component: FaqPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      {
        path: 'terms',
        component: TermsPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      {
        path: 'contact',
        component: ContactUsPageComponent,
        canActivate: [PopulationGuard],
        data: {
          exclude: ['Ticket Transfer'],
          popRedirect: 'ticketTransfer',
        },
      },
      {
        path: 'contacts',
        component: ContactsPageComponent,
        resolve: { addressBookResolver: AddressBookResolver },
      },

      // Club Pages
      {
        path: 'checkout',
        children: [
          {
            path: '',
            component: CartCheckoutPageComponent,
            resolve: {
              member: MemberResolver,
              address: AddressResolver,
              country: CountryResolver,
            },
          },
          {
            path: 'success',
            component: CartCheckoutSuccessPageComponent,
          },
          {
            path: 'results',
            component: CartCheckoutResultsPageComponent,
          },
        ],
        resolve: {
          address: AddressResolver,
        },
      },
      {
        path: 'ticketTransfer',
        children: [
          {
            path: '',
            component: TicketTransferPageComponent,
            resolve: {
              ticketResolver: TicketResolver,
            },
            canActivate: [PopulationGuard],
            data: {
              include: ['Ticket Transfer'],
              popRedirect: '',
            },
          },
          {
            path: 'tickets/:gameNumber',
            children: [
              {
                path: '',
                component: TicketsPageComponent,
              },
              {
                path: 'transfer',
                component: TransferConfirmPageComponent,
                resolve: { addressBookResolver: AddressBookResolver },
                canActivate: [ticketTransferGuard()],
              },
            ],
            resolve: {
              ticketResolver: TicketResolver,
            },
          },
          { path: 'success', component: TicketTransferSuccessPageComponent },
        ],
        resolve: {
          gameWithTickets: gameWithTicketResolver(),
        },
      },
      { path: '404page', component: NotFoundPageComponent },
    ],
  },
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  { path: 'login', component: LoginPageComponent, resolve: { preview: PreviewResolver }, canActivate: [MaintenanceGuard] },
  { path: 'login/accounts', component: LoginAccountsPageComponent, canActivate: [MaintenanceGuard] },
  {
    path: 'register',
    component: RegistrationLayoutPageComponent,
    loadChildren: () => import('@fgb/portal-component-library/src/lib/registration').then((m) => m.RegistrationModule),
    providers: [provideRegistrationConfig()],
  },
  { path: 'activate-account', component: AccountVerifiedPageComponent },
  { path: 'account-verification', component: ActivateAccountPageComponent },
  { path: 'sso', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId', component: SingleSignOnComponent },
  { path: 'login/singleSignOn/:providerId/:clubId', component: SingleSignOnComponent },
  { path: 'login/ss/login.aspx', component: V1SingleSignOnComponent },
  { path: 'maintenance', component: MaintenancePageComponent },
  { path: 'kc-callback', component: KeyCloakCallbackComponent },
  { path: 'sf-callback', component: SalesForceCallbackComponent },
  { path: '**', redirectTo: '404page' },
  { path: 'callback', component: AuthCallbackComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
      preloadingStrategy: FGBPreLoadingStrategy,
      bindToComponentInputs: true,
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
